import intl from 'react-intl-universal'
import { get, omit } from 'lodash'
import moment from 'moment'
import { debouncePromise } from '~/util/debounce'
import handleFetchApiError from '~/util/handleFetchApiError'
import settingsModel from '~/modules/services/settingsModel/index'
import {
  defaultComposerSettings,
  primaryWebsiteFieldConfig as defaultPrimaryWebsiteFieldConfig,
  overrideComposerSettings,
} from '~/modules/settings/defaultCompanySettings'

function fetchGlobalSettings() {
  return fetch('/settings-readonly/api/v1/organization', {
    credentials: 'same-origin',
  }).then((response) => {
    if (!response.ok) {
      handleFetchApiError(response)
    }
    return response.json()
  })
}

export async function _useGlobalTimezone(settingsResponse, toastr) {
  try {
    const globalSettings = await fetchGlobalSettings()
    let timezone = get(globalSettings, 'extra.timezone')
    let timeHourFormat = get(globalSettings, 'extra.timeHourFormat')
    if (!timezone) {
      timezone = moment.tz.guess()
      timeHourFormat = 12
    }

    settingsResponse.data = {
      ...settingsResponse.data,
      clockFormat: timeHourFormat.toString(),
      timezone,
    }
    return settingsResponse
  } catch (error) {
    toastr.error(intl.get('settings.globalSettings.error'))
    return settingsResponse
  }
}

export const _migrateQuickPickTimes = (response) => {
  /**
   * Convert "timePickers" into clearer, more consistant format
   *
   * {
   *  publish: [{ dayOffset: number, time: string (e.g., 19:00) }]
   *  schedule: ^^^ same as above ^^^
   * }
   */
  response.data.quickPickTimes = {
    publish: [
      {
        dayOffset: parseInt(
          get(response.data, 'timePickers.pp-firstpick-day'),
          10
        ),
        time: get(response.data, 'timePickers.pp-firstpick-time'),
      },
      {
        dayOffset: parseInt(
          get(response.data, 'timePickers.pp-secondpick-day'),
          10
        ),
        time: get(response.data, 'timePickers.pp-secondpick-time'),
      },
    ],
    scheduled: [
      {
        dayOffset: parseInt(
          get(response.data, 'timePickers.sch-firstpick-day'),
          10
        ),
        time: get(response.data, 'timePickers.sch-firstpick-time'),
      },
      {
        dayOffset: parseInt(
          get(response.data, 'timePickers.sch-secondpick-day'),
          10
        ),
        time: get(response.data, 'timePickers.sch-secondpick-time'),
      },
    ],
  }

  return response
}

export const _migrateFeaturedMediaOption = (response) => {
  if (!response || !response.data || response.data.featuredMedia) {
    return response
  }
  response.data.featuredMedia = {
    basic: {
      types: ['image'],
      label: 'Basic',
      requiredToPublish: get(response.data, 'promoItems.required.basic', false),
    },
  }

  const showBright = get(response.data, 'promoItems.bright', false)
  const brightRequired = get(response.data, 'promoItems.required.bright', false)
  if (showBright || brightRequired) {
    response.data.featuredMedia.bright = {
      types: ['image'],
      label: 'Bright',
      requiredToPublish: brightRequired,
    }
  }
  // Delete promoItems since we're not using it anymore
  delete response.data.promoItems
  return response
}

export function _migrateLineSettings(response) {
  if (
    !response ||
    !response.data ||
    get(response, 'data.lineSettings.default')
  ) {
    return response
  }

  if (response.data.wordCount) {
    // settings <= v1.25
    response.data.lineSettings = {
      default: {
        // default to words per line
        unit: 'words',
        value: get(response.data, 'wordCount.wordsPerLine', null),
        linesPerInch: get(response.data, 'wordCount.linesPerInch', null),
      },
      websites: {},
    }

    delete response.data.wordCount
  } else if (
    response.data.lineSettings &&
    !response.data.lineSettings.default
  ) {
    // settings v1.26
    response.data.lineSettings = {
      default: {
        ...response.data.lineSettings,
      },
      websites: {},
    }
  }

  return response
}

export function _migrateProofreaderSettings(response) {
  if (response.data.proofreaders) {
    return response
  }

  function migrateTansaSettings(response) {
    if (
      get(response, 'data.tansa.websiteUserIds') &&
      get(response, 'data.tansa.globalUserId') !== undefined
    ) {
      return response
    }

    response.data.tansa = {
      companyId: get(response, 'data.tansa.companyId', ''),
      globalUserId: get(response, 'data.tansa.globalId', ''),
      websiteUserIds: {},
    }

    return response
  }

  const responseWithTansa = migrateTansaSettings(response)
  response.data.proofreaders = {
    active: 'tansa',
    tansa: responseWithTansa.data.tansa,
    proLexis: {
      key: '',
      url: '',
    },
  }
  delete response.data.tansa
  delete response.data.tanza

  return response
}

function settings(toastr, root, pharosService) {
  const saveToRoot = (namespace, data) => {
    if (namespace === 'picklists') {
      root.picklists = data
    } else if (namespace === 'org') {
      root.globalSettings = data
      root.globalSettingsModel = settingsModel(data)
    }
  }

  const orgSettingsLoaded = (data, force) => {
    // Do not dispatch SETTINGS_LOADED twice if orgSettingsLoaded called from the fetchSettingsFromArcSettings

    if (!force && window.org_settings) {
      return
    }
    // 05/09/2019 - set settings on window for backwards compat with angular code

    window.org_settings = data

    root.$broadcast('SETTINGS_LOADED', {
      data: data,
    })
  }

  const settingsHolder = {
    initialized: false,
    settings: null,
    promise: null,
  }

  const mapTypeToKey = (type) => {
    return type === 'org' ? 'settings' : type
  }

  const fetchSettingsFromArcSettings = (force) => {
    if (force) {
      // Delete cached settings but not the pending promise.
      // Having pending promise and following force request is okay since fresh data will be loaded anyway
      delete settingsHolder.settings
    }

    if (settingsHolder.initialized && settingsHolder.settings) {
      return Promise.resolve(settingsHolder.settings)
    }

    // If we have pending promise, return it instead of making a new network call
    if (settingsHolder.promise) {
      return settingsHolder.promise
    }

    settingsHolder.promise = fetchGlobalSettings()
      .then((globalSettings) => {
        let timezone = get(globalSettings, 'extra.timezone')
        let timeHourFormat = get(globalSettings, 'extra.timeHourFormat')
        if (!timezone) {
          timezone = moment.tz.guess()
          timeHourFormat = 12
        }

        // If no settings data from Arc Settings, treat it as settings not initialized
        settingsHolder.initialized = !!get(globalSettings, 'ellipsis.settings')
        settingsHolder.promise = null
        settingsHolder.settings = {
          ...globalSettings.ellipsis,
          settings: {
            ...overrideComposerSettings, // Override with default settings in case of undefined
            ...globalSettings.ellipsis.settings,
            clockFormat: timeHourFormat.toString(),
            timezone,
          },
        }

        if (!settingsHolder.initialized) {
          // If Composer has no settings (not-provisioned), apply minimum settings required
          // so manual provision step can be skipped before using Composer
          settingsHolder.settings = {
            settings: {
              ...defaultComposerSettings,
              ...settingsHolder.settings.settings,
            },
          }
        }

        // Website components are fixed to circulate tab in pubstack environment
        settingsHolder.settings.settings.v2.metas = settingsHolder.settings.settings.v2.metas.filter(
          (item) => item.key !== 'section'
        )
        settingsHolder.settings.settings.v2.planning = settingsHolder.settings.settings.v2.planning.filter(
          (item) => item.key !== 'section'
        )

        // 2. Primary website selector is fixed to top of the planning tab
        if (
          settingsHolder.settings.settings.v2.planning[0] &&
          settingsHolder.settings.settings.v2.planning[0].key !==
            'canonical_website'
        ) {
          const primaryWebsiteFieldConfigMetaIndex = settingsHolder.settings.settings.v2.metas.findIndex(
            (item) => item.key === 'canonical_website'
          )
          const primaryWebsiteFieldConfigPlanningIndex = settingsHolder.settings.settings.v2.planning.findIndex(
            (item) => item.key === 'canonical_website'
          )

          if (primaryWebsiteFieldConfigMetaIndex > -1) {
            // Configured in Meta tab
            settingsHolder.settings.settings.v2.metas.splice(
              primaryWebsiteFieldConfigMetaIndex,
              1
            )
          } else if (primaryWebsiteFieldConfigPlanningIndex > -1) {
            // Configured in Planning tab
            settingsHolder.settings.settings.v2.planning.splice(
              primaryWebsiteFieldConfigPlanningIndex,
              1
            )
          }

          settingsHolder.settings.settings.v2.planning.unshift(
            defaultPrimaryWebsiteFieldConfig
          )
        }

        const res = {
          data: settingsHolder.settings.settings,
        }
        _migrateFeaturedMediaOption(res)
        _migrateQuickPickTimes(res)
        _migrateLineSettings(res)
        _migrateProofreaderSettings(res)
        return settingsHolder.settings
      })
      .then((ellipsisSettings) => {
        orgSettingsLoaded(ellipsisSettings.settings, force)
        // Some orgs have picklists saved like {picklists: {}} rather than {picklists: {picklists: {...}}}
        const cleanPicklists =
          ellipsisSettings &&
          ellipsisSettings.picklists &&
          ellipsisSettings.picklists.picklists

        return cleanPicklists
          ? ellipsisSettings
          : {
              ...ellipsisSettings,
              // Replace missing or garbage picklists
              picklists: {
                picklists: {},
              },
            }
      })
      .catch((error) => {
        settingsHolder.promise = null
        toastr.error(intl.get('settings.loadError'))
        pharosService.sendExceptionToSentry(
          error,
          'Failed Request to Global Settings'
        )
      })
    return settingsHolder.promise
  }

  const fetchSettings = (type, force) => {
    const forceFetch = force === true
    return fetchSettingsFromArcSettings(forceFetch).then((settings) => {
      return {
        data: settings[mapTypeToKey(type)],
      }
    })
  }

  this.saveSettingsToArcSettings = (type, data) => {
    // Update settings object key
    settingsHolder.settings = {
      // Default empty picklists for first time save
      picklists: {
        picklists: {},
      },
      ...settingsHolder.settings,
      [mapTypeToKey(type)]: data,
    }

    // Need to remove templates before saving to Arc Settings
    const settingsData = omit(settingsHolder.settings, ['settings.templates'])

    return fetch('/settings/api/v1/organization/ellipsis', {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settingsData),
    })
      .then((response) => {
        if (!response.ok) {
          throw response
        }
        return response.json()
      })
      .then((response) => {
        // Update settings object with the server response to make optimistic locking works
        settingsHolder.settings = response
        // Emulate $http response layout for the debouncedSave logic
        return {
          data: settingsHolder.settings[mapTypeToKey(type)],
        }
      })
  }

  const saveSettings = (type, data) => {
    return this.saveSettingsToArcSettings(type, data)
  }

  const debouncedSave = debouncePromise({
    fn: saveSettings,
    delay: 3000,
    rejected: { debounce: 'debounced settings save' },
    before: () => toastr.info(intl.get('settings.debouncedSave')),
  })

  this.fetchCompanySettings = (type = 'org') => {
    return fetchSettings(type, false).then((res) => {
      saveToRoot(type, res.data)
      return res
    })
  }

  this.fetchSettings = (force) => fetchSettings('org', force)

  this.fetchPicklists = () => fetchSettings('picklists')

  this.fetchTagsSettings = () => {
    return fetch('/settings-readonly/api/v1/organization/tags', {
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) {
          throw Error(
            `Settings request rejected with status ${response.status}`
          )
        }
        return response.json()
      })
      .catch(() => {
        throw new Error('Request to Global Settings failed')
      })
  }

  this.saveCompanySettings = (type = 'org', data, toast) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { timezone, ...dataNoTimezone } = data

    return debouncedSave(type, dataNoTimezone)
      .then((res) => {
        if (type === 'org') {
          root.globalSettings = res.data
        }
        if (type === 'picklists') {
          root.picklists = res.data
        }
        if (!toast) {
          toastr.clear()
          toastr.success(intl.get('settings.saveCompanySettings.success'))
        } else if (typeof toast === 'function') {
          toast()
        }
        return res
      })
      .catch((e) => {
        if (e.debounce) {
        } else {
          let errorMessage
          switch (e.status) {
            case 400:
              errorMessage = intl.get(
                'settings.saveCompanySettings.reloadPrompt'
              )
              break
            case 423:
              errorMessage = intl.get(
                'settings.saveCompanySettings.errorLocked'
              )
              break
            default:
              intl.get('settings.saveCompanySettings.errorDefault', {
                errorCode: e.status,
                errorMessage: e.statusText,
              })
              break
          }

          toastr.error(
            errorMessage,
            intl.get('settings.saveCompanySettings.errorTitle'),
            { allowHtml: true }
          )
          console.log(e)
        }
      })
  }

  // This method is used by settingsSyncEpic to retrofit settings object
  // updated by Redux by broadcasting SETTINGS_RETROFIT message
  // If active settings controller (UI) is mounted, it should update it's state
  // That way, React components may update settings through Redux and
  // settings controller will hold latest version as well
  // NOTE: This should be removed as soon as Angular settings page will be deprecated
  this.retrofitSettingsToSettingsController = (settings) => {
    root.$broadcast('SETTINGS_RETROFIT', {
      settings,
    })
  }
}

export default ['toastr', '$rootScope', 'pharosService', settings]
